/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
	BadgeCheck,
	Factory,
	Gauge,
	Layers,
	Ruler,
	Weight,
	Shield,
	Flame,
	Droplets,
	Wrench,
	HardDrive,
	Fan,
	Paintbrush,
	MapPin,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

type Project = {
	title: string;
	application?: string;
	capacity?: string;
	image?: string;
	details?: Record<string, string>;
	aplication?: string[];
};

type ProjectCardProps = {
	project: Project;
	index: number;
};

const getSpecIcon = (label: string) => {
	const lowerLabel = label.toLowerCase();
	if (lowerLabel.includes("weight") || lowerLabel.includes("mass"))
		return <Weight className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("diameter") || lowerLabel.includes("length"))
		return <Ruler className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("certification") || lowerLabel.includes("stamp"))
		return <Shield className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("tube") || lowerLabel.includes("shell"))
		return <Layers className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("flow") || lowerLabel.includes("rate"))
		return <Droplets className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("standard") || lowerLabel.includes("design"))
		return <Flame className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("fan") || lowerLabel.includes("motor"))
		return <Fan className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("painting") || lowerLabel.includes("coating"))
		return <Paintbrush className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("inspection") || lowerLabel.includes("agency"))
		return <Wrench className="h-3.5 w-3.5" />;
	if (lowerLabel.includes("country") || lowerLabel.includes("location"))
		return <MapPin className="h-3.5 w-3.5" />;
	return <HardDrive className="h-3.5 w-3.5" />;
};

const GLOW_COLOR = "240, 154, 56";

export default function ProjectCard({ project }: ProjectCardProps) {
	const { t } = useTranslation();
	const {
		title,
		application,
		capacity,
		image,
		details = {},
		aplication = [],
	} = project;
	const cardRef = useRef<HTMLDivElement>(null);

	const handleMove = (e: React.MouseEvent<HTMLElement>) => {
		const rect = cardRef.current?.getBoundingClientRect();
		if (!rect) return;
		const x = ((e.clientX - rect.left) / rect.width) * 100;
		const y = ((e.clientY - rect.top) / rect.height) * 100;
		cardRef.current?.style.setProperty("--glow-x", `${x}%`);
		cardRef.current?.style.setProperty("--glow-y", `${y}%`);
		cardRef.current?.style.setProperty("--glow-intensity", "1");
	};

	const handleLeave = () => {
		cardRef.current?.style.setProperty("--glow-intensity", "0");
	};

	useEffect(() => {
		const el = cardRef.current;
		if (!el) return;

		gsap.fromTo(
			el,
			{ opacity: 0, y: 50 },
			{
				opacity: 1,
				y: 0,
				duration: 0.7,
				ease: "power3.out",
				scrollTrigger: {
					trigger: el,
					start: "top 90%",
					toggleActions: "play none none none",
				},
			},
		);
	}, []);

	const hasCertification = Object.keys(details).some(
		(key) =>
			key.toLowerCase().includes("certification") ||
			key.toLowerCase().includes("stamp"),
	);

	const country =
		details["Country"] ||
		details["country"] ||
		details[t("worldwidePage.labels.country")] ||
		"";

	const primarySpecs: [string, string][] = [];
	const otherSpecs: [string, string][] = [];

	Object.entries(details).forEach(([key, value]) => {
		const lowerKey = key.toLowerCase();
		if (lowerKey === "country" || lowerKey === "country") return;
		if (
			lowerKey.includes("certification") ||
			lowerKey.includes("stamp") ||
			lowerKey.includes("design standard") ||
			lowerKey.includes("standard")
		) {
			primarySpecs.push([key, value]);
		} else {
			otherSpecs.push([key, value]);
		}
	});

	const sortedSpecs = [...primarySpecs, ...otherSpecs];

	return (
		<motion.article
			ref={cardRef}
			initial={{ opacity: 0, y: 40 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-50px" }}
			transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
			onMouseMove={handleMove}
			onMouseLeave={handleLeave}
			className="project-card group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-[#2d2f92]/30 hover:shadow-2xl">
			{/* Spotlight overlay */}
			<div className="pointer-events-none absolute inset-0 z-30 spotlight-glow" />
			{/* Border glow */}
			<div className="pointer-events-none absolute inset-0 z-40 border-glow" />
			{/* TOP - FULL IMAGE */}
			<div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
				{image ?
					<img
						src={image}
						alt={title}
						className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
					/>
				:	<div className="flex h-full w-full items-center justify-center">
						<Factory className="h-16 w-16 text-slate-400" />
					</div>
				}

				{/* Gradient overlay */}
				<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

				{/* Top badges */}
				<div className="absolute left-3 right-3 top-3 flex items-start justify-between gap-2">
					{/* {capacity && (
						<div className="flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-slate-800 shadow-md backdrop-blur-sm">
							<Gauge className="h-3.5 w-3.5 text-[#2d2f92]" />
							<span>{capacity}</span>
						</div>
					)} */}
					{/* {hasCertification && (
						<span className="ml-auto flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-md backdrop-blur-sm">
							<BadgeCheck className="h-3 w-3" />
							Certified
						</span>
					)} */}
				</div>

				{/* Bottom title overlay */}
				<div className="absolute bottom-0 left-0 right-0 p-4">
					{country && (
						<div className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white backdrop-blur-sm">
							<MapPin className="h-3 w-3" />
							{country}
						</div>
					)}
					<h3 className="text-lg font-bold leading-tight text-white drop-shadow-md">
						{title}
						{/* , {country} */}
					</h3>
				</div>
			</div>

			{/* BOTTOM - DETAILS */}
			<div className="relative flex flex-1 flex-col p-4">
				{/* Decorative accent line */}
				<div className="absolute left-0 right-0 top-0 h-[3px] bg-gradient-to-r from-[#2d2f92] via-[#7c3aed] to-[#f09a38]" />

				{application && (
					<div className="mb-3 flex items-start gap-2.5 rounded-lg border border-[#2d2f92]/15 bg-gradient-to-br from-[#2d2f92]/5 via-white to-[#f09a38]/5 px-3 py-2.5">
						<span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#2d2f92] to-[#5b3fc4] text-white shadow-sm">
							<Flame className="h-3.5 w-3.5" />
						</span>
						<div className="min-w-0 flex-1">
							<span className="block text-[9px] font-bold uppercase tracking-wider text-[#2d2f92]">
								{t("projectCard.application")}
							</span>
							<span className="mt-0.5 block text-xs font-semibold leading-tight text-slate-800">
								{application}
							</span>
						</div>
					</div>
				)}

				{/* Tags */}
				{aplication && aplication.length > 0 && (
					<div className="mb-3 flex flex-wrap gap-1.5">
						{aplication.map((tag) => (
							<span
								key={tag}
								className="rounded-full border border-[#2d2f92]/20 bg-[#2d2f92]/5 px-2.5 py-0.5 text-[10px] font-semibold text-[#2d2f92]">
								{tag}
							</span>
						))}
					</div>
				)}

				{/* Specs header */}
				<div className="mb-2.5 flex items-center gap-2 border-t border-dashed border-slate-200 pt-2.5">
					<span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-[#2d2f92] to-[#f09a38] text-white shadow-sm">
						<BadgeCheck className="h-3 w-3" />
					</span>
					<h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
						{t("projectCard.technicalSpecifications")}
					</h4>
					<span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-600">
						{sortedSpecs.length}
					</span>
				</div>

				{/* All specs - 2-column compact grid */}
				{sortedSpecs.length > 0 ?
					<dl className="grid flex-1 grid-cols-2 gap-1.5">
						{sortedSpecs.map(([key, value]) => (
							<div
								key={key}
								className="group/spec relative flex items-start gap-1.5 overflow-hidden rounded-md border border-slate-100 bg-gradient-to-br from-slate-50 to-white px-2 py-1.5 transition-all hover:border-[#2d2f92]/30 hover:shadow-sm">
								<span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-white text-[#2d2f92] shadow-sm ring-1 ring-slate-200">
									{getSpecIcon(key)}
								</span>
								<div className="min-w-0 flex-1">
									<dt
										className="block truncate text-[8.5px] font-bold uppercase tracking-wide text-slate-500"
										title={key}>
										{key}
									</dt>
									<dd
										className="mt-0.5 block break-words text-[11px] font-semibold leading-tight text-slate-800"
										title={value}>
										{value}
									</dd>
								</div>
							</div>
						))}
					</dl>
				:	<p className="text-xs italic text-slate-500">
						{t("projectCard.noSpecs")}
					</p>
				}

				{/* Footer accent */}
				<div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
					<span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">
						<Factory className="h-3 w-3" />
						{t("projectCard.heatExchanger")}
					</span>
					<div className="flex items-center gap-1">
						<span className="h-1.5 w-1.5 rounded-full bg-[#2d2f92]" />
						<span className="h-1.5 w-1.5 rounded-full bg-[#7c3aed]" />
						<span className="h-1.5 w-1.5 rounded-full bg-[#f09a38]" />
					</div>
				</div>
			</div>

			<style>{`
				.project-card {
					--glow-x: 50%;
					--glow-y: 50%;
					--glow-intensity: 0;
				}
				.project-card .spotlight-glow {
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(${GLOW_COLOR}, calc(var(--glow-intensity) * 0.18)),
						transparent 60%
					);
					transition: opacity 0.25s ease;
				}
				.project-card .border-glow::before {
					content: "";
					position: absolute;
					inset: 0;
					border-radius: inherit;
					padding: 1.5px;
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(${GLOW_COLOR}, calc(var(--glow-intensity) * 0.85)),
						transparent 55%
					);
					-webkit-mask:
						linear-gradient(#000 0 0) content-box,
						linear-gradient(#000 0 0);
					-webkit-mask-composite: xor;
					mask-composite: exclude;
					opacity: var(--glow-intensity);
					transition: opacity 0.25s ease;
				}
				.project-card::after {
					content: "";
					position: absolute;
					inset: -2px;
					border-radius: inherit;
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(${GLOW_COLOR}, calc(var(--glow-intensity) * 0.35)),
						transparent 60%
					);
					filter: blur(14px);
					opacity: var(--glow-intensity);
					z-index: -1;
					transition: opacity 0.25s ease;
					pointer-events: none;
				}
			`}</style>
		</motion.article>
	);
}
